import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { UploadWizardSteps } from './upload-wizard-steps'
import { AddFilesStep } from './steps/add-files-step'
import { DetailsStep } from './steps/details-step'
import { GeoreferenceStep } from './steps/georeference-step'
import { PermissionsStep } from './steps/permissions-step'
import { DoneStep } from './steps/done-step'
import { FileUploadStatus, useUploadDocuments } from '../../hooks/useDocumentUpload'
import {
  DEFAULT_UPLOAD_VALUES,
  DocumentUploadFormData,
  DocumentUploadSchema,
  WIZARD_STEPS,
  WizardStep,
} from '../../documents-upload.types'

type UploadWizardDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const STEP_KEYS = Object.keys(WIZARD_STEPS) as WizardStep[]

type FileStatusEntry = { title: string; status: FileUploadStatus; errorMessage: string | null }

export const UploadWizardDialog = ({ open, onOpenChange }: UploadWizardDialogProps) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('files')
  const [fileStatuses, setFileStatuses] = useState<FileStatusEntry[]>([])
  const uploadDocuments = useUploadDocuments()

  const form = useForm<DocumentUploadFormData>({
    resolver: zodResolver(DocumentUploadSchema),
    mode: 'onChange',
    defaultValues: DEFAULT_UPLOAD_VALUES,
  })

  useEffect(() => {
    if (!open) {
      form.reset(DEFAULT_UPLOAD_VALUES)
      setCurrentStep('files')
      setFileStatuses([])
    }
    // Only reset when the dialog closes, not on every form-identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const currentIndex = STEP_KEYS.indexOf(currentStep)
  const isUploading = currentStep === 'done' && uploadDocuments.isPending
  const isDone = currentStep === 'done' && !uploadDocuments.isPending && fileStatuses.length > 0

  const validateStep = async (step: WizardStep): Promise<boolean> => {
    switch (step) {
      case 'files':
        return form.trigger('files')
      case 'details':
        return form.trigger(['files', 'category'])
      case 'permissions':
        return form.trigger(['visibility', 'roleIds'])
      default:
        return true
    }
  }

  const submit = () => {
    const values = form.getValues()
    setFileStatuses(
      values.files.map((f) => ({ title: f.title, status: 'idle', errorMessage: null })),
    )
    setCurrentStep('done')
    uploadDocuments.mutate({
      form: values,
      onFileStatusChange: (index, status, errorMessage) => {
        setFileStatuses((prev) =>
          prev.map((f, i) =>
            i === index ? { ...f, status, errorMessage: errorMessage ?? null } : f,
          ),
        )
      },
    })
  }

  const handleNext = async () => {
    const valid = await validateStep(currentStep)
    if (!valid) return

    if (currentStep === 'permissions') {
      submit()
      return
    }
    setCurrentStep(STEP_KEYS[currentIndex + 1])
  }

  const handleBack = () => {
    if (currentIndex > 0) setCurrentStep(STEP_KEYS[currentIndex - 1])
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col overflow-hidden gap-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle>Upload document</DialogTitle>
        </DialogHeader>

        {currentStep !== 'done' && (
          <div className="border-b px-6 py-4">
            <UploadWizardSteps
              steps={WIZARD_STEPS}
              currentStep={currentStep}
              completedSteps={Object.fromEntries(
                STEP_KEYS.map((step, i) => [step, i < currentIndex]),
              )}
              onSelectStep={(step) =>
                STEP_KEYS.indexOf(step) < currentIndex && setCurrentStep(step)
              }
            />
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 'files' && <AddFilesStep form={form} />}
          {currentStep === 'details' && <DetailsStep form={form} />}
          {currentStep === 'georeference' && (
            <GeoreferenceStep
              value={form.watch('georeference')}
              onChange={(value) => form.setValue('georeference', value, { shouldDirty: true })}
            />
          )}
          {currentStep === 'permissions' && (
            <PermissionsStep
              visibility={form.watch('visibility')}
              roleIds={form.watch('roleIds')}
              onChange={({ visibility, roleIds }) => {
                form.setValue('visibility', visibility, { shouldDirty: true })
                form.setValue('roleIds', roleIds, { shouldValidate: true, shouldDirty: true })
              }}
              rolesError={form.formState.errors.roleIds?.message}
            />
          )}
          {currentStep === 'done' && (
            <DoneStep files={fileStatuses} isComplete={!uploadDocuments.isPending} />
          )}
        </div>

        {currentStep !== 'done' && (
          <div className="flex justify-between p-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={currentIndex === 0}
            >
              Back
            </Button>
            <Button type="button" onClick={handleNext}>
              {currentStep === 'permissions' ? 'Upload' : 'Next'}
            </Button>
          </div>
        )}

        {(isUploading || isDone) && (
          <div className="flex justify-end p-4 border-t">
            <Button type="button" onClick={() => onOpenChange(false)} disabled={isUploading}>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
